import {
  aggregateByProductLineWithSKUs,
  aggregateChildrenToParents,
  aggregateDuplicateClassOfTrade,
  max_store_count,
} from "./aggregation"
import {
  OpportunityScenarioView,
  applyOpportunityScenario,
  meshOpportunitiesIntoRelatedRows,
  pruneOpenOpportunityBranches,
  spliceOpportunityBranchesIntoExistingCustomers,
} from "./opportunities"
import type { ColumnConfig, SalesDataPoint, SalesHierarchyNode } from "../types"
import { HierarchyLevel, ViewMode } from "../types"
import { aggregateByShipToDistributor, type ShipToFallback } from "./ship_to_view"
import { metrics_part_cases } from "./quantity_basis"
import {
  get_display_cases,
  get_display_units,
  get_display_volume,
  promo_lift_dollars_for_datapoint,
  promo_lift_units_for_datapoint,
  select_primary_sales_rows,
} from "./forecast_measure_slice"

/** Matches server leaf-primary split: actuals are `source_kind === "actual"` (with client rows using `is_actual` when unset). */
function is_actual_volume_point(dp: SalesDataPoint): boolean {
  if (dp.source_kind === "actual") return true
  if (dp.is_actual) return true
  return false
}

/**
 * Whether any row in the plan is an actual — i.e. whether Compare has anything to show.
 *
 * A pure plan year (every Confido import, any freshly cloned scenario) has no actuals, so
 * Compare renders a view identical to Forecast; the toolbar uses this to hide the toggle
 * rather than offer a control that does nothing. Walks with early exit, so it costs nothing
 * on the large versions where the answer is "yes" immediately.
 */
export function hierarchyHasActuals(roots: SalesHierarchyNode[]): boolean {
  function walk(node: SalesHierarchyNode): boolean {
    if (Array.isArray(node.sales_data) && node.sales_data.some(is_actual_volume_point)) {
      return true
    }
    return Array.isArray(node.children) && node.children.some((child: any) => walk(child as SalesHierarchyNode))
  }
  return roots.some(walk)
}

/**
 * Leaf-only actual vs remaining forecast, one primary value per `(leaf, period)`:
 * if any row is actual, only actual rows for that cell count; otherwise forecast rows count.
 * Matches server `leaf_primary_totals_from_version_rows` (avoids double-count when both exist).
 *
 * When `period_keys` is set, only those `period_key`s are included — **same months as the table footer**
 * (plan year columns). Omit or pass null to sum every month present on leaves.
 */
const DEFAULT_COLUMN_SLICE: ColumnConfig = {
  show_stores: true,
  show_cases: true,
  show_volume: true,
}

export function summarizeForecastHierarchyTotals(
  roots: SalesHierarchyNode[],
  period_keys?: string[] | null,
  column_config?: ColumnConfig
): {
  actual_volume_dollars: number
  forecast_volume_dollars: number
  total_cases_sold: number
  /** Leaf-primary sums of imputed promo revenue lift (matches table footer promo line). */
  total_promo_lift_dollars: number
  /** Leaf-primary sums of incremental promo units from detail. */
  total_promo_lift_units: number
} {
  const period_filter =
    period_keys != null && period_keys.length > 0 ? new Set(period_keys) : null
  const cfg = column_config ?? DEFAULT_COLUMN_SLICE

  let actual_volume_dollars = 0
  let forecast_volume_dollars = 0
  let total_cases_sold = 0
  let total_promo_lift_dollars = 0
  let total_promo_lift_units = 0

  walkHierarchyLeaves(roots, (node: SalesHierarchyNode) => {
    const by_period = new Map<string, SalesDataPoint[]>()
    for (const dp of select_primary_sales_rows(node.sales_data)) {
      const k = dp.period?.period_key
      if (!k) continue
      if (period_filter && !period_filter.has(k)) continue
      const list = by_period.get(k) ?? []
      list.push(dp)
      by_period.set(k, list)
    }
    for (const [, dps] of by_period) {
      const actual_dps = dps.filter(is_actual_volume_point)
      const forecast_dps = dps.filter((d) => !is_actual_volume_point(d))
      const actual_vol = actual_dps.reduce((s, d) => s + get_display_volume(d, cfg), 0)
      const forecast_vol = forecast_dps.reduce((s, d) => s + get_display_volume(d, cfg), 0)
      const actual_cases = actual_dps.reduce((s, d) => s + get_display_cases(d, cfg), 0)
      const forecast_cases = forecast_dps.reduce((s, d) => s + get_display_cases(d, cfg), 0)
      if (actual_dps.length > 0) {
        actual_volume_dollars += actual_vol
        total_cases_sold += actual_cases
        for (const d of actual_dps) {
          total_promo_lift_dollars += promo_lift_dollars_for_datapoint(d, cfg)
          total_promo_lift_units += promo_lift_units_for_datapoint(d)
        }
      } else {
        forecast_volume_dollars += forecast_vol
        total_cases_sold += forecast_cases
        for (const d of forecast_dps) {
          total_promo_lift_dollars += promo_lift_dollars_for_datapoint(d, cfg)
          total_promo_lift_units += promo_lift_units_for_datapoint(d)
        }
      }
    }
  })

  return {
    actual_volume_dollars,
    forecast_volume_dollars,
    total_cases_sold,
    total_promo_lift_dollars,
    total_promo_lift_units,
  }
}

/**
 * Same transformations as `processedData` in the sales forecasting page — keeps expansion paths aligned with the table tree.
 */
export function buildForecastTableHierarchy(
  data: SalesHierarchyNode[],
  viewMode: ViewMode,
  opportunityScenarioView: OpportunityScenarioView,
  selectedOpportunityIds: Set<string>,
  options?: {
    /**
     * `false` while the Opportunities panel is closed: open deals disappear from the grid
     * and the totals entirely (not just zeroed), so the default view is the committed plan.
     */
    include_open_opportunities?: boolean
    /**
     * Ship-to view only: the distributor each account buys through per the brand's customer
     * records, used for customer nodes that carry no `metadata.rtm` split.
     */
    ship_to_fallback?: ShipToFallback
  }
): SalesHierarchyNode[] {
  // Parents were aggregated at load with deal volume included, so after pruning they must be
  // rebuilt from their surviving children — otherwise a customer row keeps showing volume its
  // children no longer carry (Target: a $7k parent over all-zero baseline SKUs).
  // pruneNode preserves node identity when nothing under it was removed, so element identity
  // is a reliable "no opportunity branches existed" signal — the common case (most versions
  // carry no open deals), where the full-forest re-aggregate would be pure waste per edit.
  let base = data
  if (options?.include_open_opportunities === false) {
    const pruned = pruneOpenOpportunityBranches(data)
    const nothing_pruned =
      pruned.length === data.length && pruned.every((node, index) => node === data[index])
    base = nothing_pruned ? data : aggregateChildrenToParents(pruned)
  }
  let opportunityAdjustedData = applyOpportunityScenario(base, opportunityScenarioView, selectedOpportunityIds)
  if (viewMode === ViewMode.CHAIN_RETAILER_VIEW) {
    opportunityAdjustedData = aggregateDuplicateClassOfTrade(opportunityAdjustedData)
  }
  const splicedOpportunityData = spliceOpportunityBranchesIntoExistingCustomers(opportunityAdjustedData)
  const meshedOpportunityData = meshOpportunitiesIntoRelatedRows(splicedOpportunityData)
  if (viewMode === ViewMode.PRODUCT_LINE_VIEW) {
    return aggregateByProductLineWithSKUs(meshedOpportunityData)
  }
  if (viewMode === ViewMode.SHIP_TO_VIEW) {
    return aggregateByShipToDistributor(meshedOpportunityData, options?.ship_to_fallback)
  }
  return meshedOpportunityData
}

/** Per-period sums across hierarchy leaves only (avoids double-counting parent rollups). */
export interface LeafMonthlyMetricSums {
  volume_dollars: number
  cases_sold: number
  /** Σ leaf part-cases, for a period whose whole cases total 0. */
  part_cases: number
  store_count: number
  /** Σ leaf store counts — total points of distribution, the velocity denominator. */
  sku_store_count: number
  units_sold: number
  promo_lift_dollars: number
  promo_lift_units: number
}

/**
 * Walks only `children`-based leaves.
 * Relationship-only shapes (`retailer_relationships` / `dc_relationships`) are intentionally not traversed here.
 */
function walkHierarchyLeaves(roots: SalesHierarchyNode[], on_leaf: (node: SalesHierarchyNode) => void): void {
  function walk(node: SalesHierarchyNode): void {
    const ch = (node as { children?: SalesHierarchyNode[] }).children
    if (Array.isArray(ch) && ch.length > 0) {
      ch.forEach(walk)
      return
    }
    on_leaf(node)
  }

  for (const root of roots) {
    walk(root)
  }
}

export function sumLeafMonthlyMetricsByPeriod(
  roots: SalesHierarchyNode[],
  periodKeys: string[],
  column_config?: ColumnConfig
): Record<string, LeafMonthlyMetricSums> {
  const sums: Record<string, LeafMonthlyMetricSums> = {}
  for (const k of periodKeys) {
    sums[k] = {
      volume_dollars: 0,
      cases_sold: 0,
      part_cases: 0,
      store_count: 0,
      sku_store_count: 0,
      units_sold: 0,
      promo_lift_dollars: 0,
      promo_lift_units: 0,
    }
  }
  const cfg = column_config ?? DEFAULT_COLUMN_SLICE

  walkHierarchyLeaves(roots, (node: SalesHierarchyNode) => {
    const by_period = new Map<string, SalesDataPoint[]>()
    for (const dp of select_primary_sales_rows(node.sales_data)) {
      const key = dp.period?.period_key
      if (!key || sums[key] === undefined) {
        continue
      }
      const list = by_period.get(key) ?? []
      list.push(dp)
      by_period.set(key, list)
    }
    for (const period_key of periodKeys) {
      const dps = by_period.get(period_key)
      if (!dps?.length) continue
      const actual_dps = dps.filter(is_actual_volume_point)
      const forecast_dps = dps.filter((d) => !is_actual_volume_point(d))
      const actual_vol = actual_dps.reduce((s, d) => s + get_display_volume(d, cfg), 0)
      const forecast_vol = forecast_dps.reduce((s, d) => s + get_display_volume(d, cfg), 0)
      const actual_cases = actual_dps.reduce((s, d) => s + get_display_cases(d, cfg), 0)
      const forecast_cases = forecast_dps.reduce((s, d) => s + get_display_cases(d, cfg), 0)
      // Doors overlap across leaves, so the footer reports the widest distribution rather than
      // a sum — same rule as the parent roll-up (see `max_store_count`).
      const actual_stores = actual_dps.reduce((s, d) => max_store_count(s, d.metrics?.store_count), 0)
      const forecast_stores = forecast_dps.reduce((s, d) => max_store_count(s, d.metrics?.store_count), 0)
      if (actual_dps.length > 0) {
        sums[period_key].volume_dollars += actual_vol
        sums[period_key].cases_sold += actual_cases
        sums[period_key].store_count = max_store_count(sums[period_key].store_count, actual_stores)
      } else {
        sums[period_key].volume_dollars += forecast_vol
        sums[period_key].cases_sold += forecast_cases
        sums[period_key].store_count = max_store_count(sums[period_key].store_count, forecast_stores)
      }
      const winning = actual_dps.length > 0 ? actual_dps : forecast_dps
      // Distribution points sum across leaves (unlike doors, which overlap): this is what
      // footer velocity divides by, so it matches the per-SKU average up the tree.
      sums[period_key].sku_store_count += winning.reduce(
        (s, d) => s + Number(d.metrics?.sku_store_count ?? d.metrics?.store_count ?? 0),
        0
      )
      for (const d of winning) {
        sums[period_key].part_cases += metrics_part_cases(d.metrics)
        const u = get_display_units(d, cfg)
        if (u !== undefined && u !== null && Number.isFinite(Number(u))) {
          sums[period_key].units_sold += Number(u)
        }
        sums[period_key].promo_lift_dollars += promo_lift_dollars_for_datapoint(d, cfg)
        sums[period_key].promo_lift_units += promo_lift_units_for_datapoint(d)
      }
    }
  })
  return sums
}

/** Dollar totals summed only over leaves that have paired compare metrics (forecast + actual on dollars axis). */
export interface ComparableDollarPeriodTotals {
  forecast_volume_dollars: number
  actual_volume_dollars: number
}

/**
 * Leaf-only sums for footer “compared rows” — same walk as `sumLeafMonthlyMetricsByPeriod`, but only
 * counts `sales_data` points with `compare_axis === "dollars"` and both `forecast_metrics` and `actual_metrics`.
 */
export function computeComparableDollarTotalsByPeriod(
  roots: SalesHierarchyNode[],
  periodKeys: string[]
): Record<string, ComparableDollarPeriodTotals> {
  const totals: Record<string, ComparableDollarPeriodTotals> = {}
  for (const period_key of periodKeys) {
    totals[period_key] = { forecast_volume_dollars: 0, actual_volume_dollars: 0 }
  }

  walkHierarchyLeaves(roots, (node: SalesHierarchyNode) => {
    for (const dp of node.sales_data ?? []) {
      const period_key = dp.period?.period_key
      if (!period_key || totals[period_key] === undefined) continue
      if (dp.compare_axis !== "dollars" || !dp.forecast_metrics || !dp.actual_metrics) continue
      totals[period_key].forecast_volume_dollars += Number(dp.forecast_metrics.volume_dollars ?? 0)
      totals[period_key].actual_volume_dollars += Number(dp.actual_metrics.volume_dollars ?? 0)
    }
  })
  return totals
}

const COMPARABLE_DOLLAR_EQUALITY_EPS = 0.005

/** Cent-safe equality for comparable actual vs forecast footer collapse. */
export function comparable_dollar_totals_are_effectively_equal(actual: number, forecast: number): boolean {
  return Math.abs(Number(actual) - Number(forecast)) < COMPARABLE_DOLLAR_EQUALITY_EPS
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("hierarchyHasActuals", () => {
    function leaf(source_kind: string, extra: Record<string, unknown> = {}): any {
      return {
        id: `sku-${source_kind}`,
        name: "SKU",
        level: HierarchyLevel.SKU,
        sales_data: [
          {
            period: { year: 2027, month: 1, period_key: "2027-01" },
            metrics: { store_count: 0, volume_dollars: 100, cases_sold: 10, case_cost: 1 },
            source_kind,
            ...extra,
          },
        ],
      }
    }

    it("is false for a pure plan year, so Compare can be hidden", () => {
      const plan = [
        { id: "c", name: "Kroger", level: HierarchyLevel.CUSTOMER, sales_data: [], children: [leaf("forecast")] },
      ] as any
      expect(hierarchyHasActuals(plan)).toBe(false)
    })

    it("finds an actual nested under any depth", () => {
      const withActual = [
        {
          id: "c",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER,
          sales_data: [],
          children: [
            { id: "pl", name: "MP", level: HierarchyLevel.PRODUCT_LINE, sales_data: [], children: [leaf("actual")] },
          ],
        },
      ] as any
      expect(hierarchyHasActuals(withActual)).toBe(true)
    })

    it("honors the client-side is_actual flag when source_kind is unset", () => {
      const clientRows = [
        { id: "c", name: "Kroger", level: HierarchyLevel.CUSTOMER, sales_data: [], children: [leaf("", { is_actual: true })] },
      ] as any
      expect(hierarchyHasActuals(clientRows)).toBe(true)
      expect(hierarchyHasActuals([])).toBe(false)
    })

    it("does not count opportunity rows as actuals", () => {
      const opportunityOnly = [
        { id: "c", name: "Kroger", level: HierarchyLevel.CUSTOMER, sales_data: [], children: [leaf("opportunity")] },
      ] as any
      expect(hierarchyHasActuals(opportunityOnly)).toBe(false)
    })
  })

  describe("summarizeForecastHierarchyTotals", () => {
    it("partitions volume by actual vs forecast using source_kind", () => {
      const forest = [
        {
          id: "r",
          name: "Root",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 1, volume_dollars: 100, cases_sold: 10, case_cost: 1 },
              source_kind: "actual",
            },
            {
              period: { year: 2026, month: 2, period_key: "2026-02" },
              metrics: { store_count: 1, volume_dollars: 50, cases_sold: 5, case_cost: 1 },
              source_kind: "forecast",
            },
          ],
          children: [],
        },
      ] as SalesHierarchyNode[]
      const s = summarizeForecastHierarchyTotals(forest)
      expect(s.actual_volume_dollars).toBe(100)
      expect(s.forecast_volume_dollars).toBe(50)
      expect(s.total_cases_sold).toBe(15)
      expect(s.total_promo_lift_dollars).toBe(0)
      expect(s.total_promo_lift_units).toBe(0)
    })

    it("when actual and forecast rows share the same leaf period, actual wins (no double count)", () => {
      const forest = [
        {
          id: "leaf",
          name: "Leaf",
          level: HierarchyLevel.SKU,
          sales_data: [
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 1, volume_dollars: 100, cases_sold: 10, case_cost: 1 },
              source_kind: "actual",
            },
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 1, volume_dollars: 999, cases_sold: 99, case_cost: 1 },
              source_kind: "forecast",
            },
          ],
          children: [],
        },
      ] as unknown as SalesHierarchyNode[]
      const s = summarizeForecastHierarchyTotals(forest)
      expect(s.actual_volume_dollars).toBe(100)
      expect(s.forecast_volume_dollars).toBe(0)
      expect(s.total_cases_sold).toBe(10)
      expect(s.total_promo_lift_dollars).toBe(0)
      expect(s.total_promo_lift_units).toBe(0)
    })

    it("when period_keys is passed, excludes other months (matches table footer / plan columns)", () => {
      const forest = [
        {
          id: "leaf",
          name: "Leaf",
          level: HierarchyLevel.SKU,
          sales_data: [
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 1, volume_dollars: 10, cases_sold: 1, case_cost: 1 },
              source_kind: "forecast",
            },
            {
              period: { year: 2027, month: 1, period_key: "2027-01" },
              metrics: { store_count: 1, volume_dollars: 9999, cases_sold: 1, case_cost: 1 },
              source_kind: "forecast",
            },
          ],
          children: [],
        },
      ] as unknown as SalesHierarchyNode[]
      const s = summarizeForecastHierarchyTotals(forest, ["2026-01"])
      expect(s.forecast_volume_dollars).toBe(10)
      expect(s.actual_volume_dollars).toBe(0)
      expect(s.total_cases_sold).toBe(1)
      expect(s.total_promo_lift_dollars).toBe(0)
      expect(s.total_promo_lift_units).toBe(0)
    })

    it("sums imputed promo lift on forecast-primary leaf months from detail", () => {
      const cfg: ColumnConfig = {
        show_stores: true,
        show_cases: true,
        show_volume: true,
        forecast_dollar_basis: "consumption_gross_sales",
        forecast_case_basis: "forecasted_consumption_cases",
      }
      const forest = [
        {
          id: "leaf",
          level: HierarchyLevel.SKU,
          sales_data: [
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: {
                store_count: 1,
                volume_dollars: 0,
                cases_sold: 0,
                case_cost: 0,
                detail: {
                  consumption_gross_sales: 1000,
                  forecasted_consumption_units: 100,
                  incremental_promotion_consumption_units: 10,
                },
              },
              source_kind: "forecast",
            },
          ],
          children: [],
        },
      ] as unknown as SalesHierarchyNode[]
      const s = summarizeForecastHierarchyTotals(forest, null, cfg)
      expect(s.total_promo_lift_dollars).toBe(100)
      expect(s.total_promo_lift_units).toBe(10)
    })

    it("keeps actual vs remaining forecast split after rollup (source_kind preserved on parents)", () => {
      const actual_dp = {
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: { store_count: 1, volume_dollars: 1000, cases_sold: 10, case_cost: 1 },
        source_kind: "actual" as const,
        is_actual: true,
        is_forecast: false,
      }
      const forecast_dp = {
        period: { year: 2026, month: 4, period_key: "2026-04" },
        metrics: { store_count: 1, volume_dollars: 500, cases_sold: 5, case_cost: 1 },
        source_kind: "forecast" as const,
        is_actual: false,
        is_forecast: true,
      }
      const forest = [
        {
          id: "cot",
          name: "COT",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "sku",
              name: "SKU",
              level: HierarchyLevel.SKU,
              sales_data: [actual_dp, forecast_dp],
              children: [],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]
      const s = summarizeForecastHierarchyTotals(forest)
      expect(s.actual_volume_dollars).toBe(1000)
      expect(s.forecast_volume_dollars).toBe(500)
      expect(s.total_promo_lift_dollars).toBe(0)
      expect(s.total_promo_lift_units).toBe(0)
    })

    it("recomputes parent rollups from children before reading roots (stale root after leaf edit)", () => {
      const leaf_dp = {
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: { store_count: 2, volume_dollars: 200, cases_sold: 20, case_cost: 1 },
        source_kind: "forecast",
      }
      const forest = [
        {
          id: "cot",
          name: "COT",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "pl",
              name: "PL",
              level: HierarchyLevel.PRODUCT_LINE,
              sales_data: [leaf_dp],
              children: [],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]
      const s = summarizeForecastHierarchyTotals(forest)
      expect(s.forecast_volume_dollars).toBe(200)
      expect(s.total_cases_sold).toBe(20)
      expect(s.total_promo_lift_dollars).toBe(0)
      expect(s.total_promo_lift_units).toBe(0)
    })
  })

  describe("sumLeafMonthlyMetricsByPeriod", () => {
    it("sums only leaves, not parent aggregates", () => {
      const forest = [
        {
          id: "p",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "l1",
              level: HierarchyLevel.SKU,
              sales_data: [
                {
                  period: { year: 2026, month: 1, period_key: "2026-01" },
                  metrics: { store_count: 1, volume_dollars: 100, cases_sold: 10, case_cost: 0 },
                },
              ],
              children: [],
            },
            {
              id: "l2",
              level: HierarchyLevel.SKU,
              sales_data: [
                {
                  period: { year: 2026, month: 1, period_key: "2026-01" },
                  metrics: { store_count: 2, volume_dollars: 50, cases_sold: 5, case_cost: 0 },
                },
              ],
              children: [],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]
      const sums = sumLeafMonthlyMetricsByPeriod(forest, ["2026-01"])
      expect(sums["2026-01"].volume_dollars).toBe(150)
      expect(sums["2026-01"].cases_sold).toBe(15)
      expect(sums["2026-01"].promo_lift_dollars).toBe(0)
      expect(sums["2026-01"].promo_lift_units).toBe(0)
    })

    it("footer-style sums use actual-wins when actual and forecast share a leaf period", () => {
      const forest = [
        {
          id: "leaf",
          level: HierarchyLevel.SKU,
          sales_data: [
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 1, volume_dollars: 100, cases_sold: 10, case_cost: 0 },
              source_kind: "actual",
            },
            {
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 1, volume_dollars: 500, cases_sold: 50, case_cost: 0 },
              source_kind: "forecast",
            },
          ],
          children: [],
        },
      ] as unknown as SalesHierarchyNode[]
      const sums = sumLeafMonthlyMetricsByPeriod(forest, ["2026-01"])
      expect(sums["2026-01"].volume_dollars).toBe(100)
      expect(sums["2026-01"].cases_sold).toBe(10)
    })
  })

  describe("computeComparableDollarTotalsByPeriod", () => {
    it("sums only paired compare leaves; full leaf sum can be larger", () => {
      const compare_dp = {
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: { store_count: 0, volume_dollars: 100, cases_sold: 0, case_cost: 0 },
        compare_axis: "dollars" as const,
        forecast_metrics: { store_count: 0, volume_dollars: 100, cases_sold: 0, case_cost: 0 },
        actual_metrics: { store_count: 0, volume_dollars: 80, cases_sold: 0, case_cost: 0 },
      }
      const forest = [
        {
          id: "p",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "mapped",
              level: HierarchyLevel.SKU,
              sales_data: [compare_dp],
              children: [],
            },
            {
              id: "unmapped",
              level: HierarchyLevel.SKU,
              sales_data: [
                {
                  period: { year: 2026, month: 1, period_key: "2026-01" },
                  metrics: { store_count: 0, volume_dollars: 200, cases_sold: 0, case_cost: 0 },
                },
              ],
              children: [],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]
      const sums = sumLeafMonthlyMetricsByPeriod(forest, ["2026-01"])
      const comp = computeComparableDollarTotalsByPeriod(forest, ["2026-01"])
      expect(sums["2026-01"].volume_dollars).toBe(300)
      expect(comp["2026-01"].forecast_volume_dollars).toBe(100)
      expect(comp["2026-01"].actual_volume_dollars).toBe(80)
    })
  })

  describe("comparable_dollar_totals_are_effectively_equal", () => {
    it("treats cent-level differences as equal", () => {
      expect(comparable_dollar_totals_are_effectively_equal(100, 100.001)).toBe(true)
      expect(comparable_dollar_totals_are_effectively_equal(100, 100.02)).toBe(false)
    })
  })
}
